import { useCallback } from 'react';
import { runConnectorParameterAddition } from './connectorInvocation';
import {
  type ParameterAdditionOptions,
  type ParameterAdditionResult,
} from './models';
import { type ParameterAdditionCore } from './useParameterAdditionCore';
import { type ParameterMutationActions } from './useParameterMutationActions';

/**
 * Binds the connector invocation runner to React. All invocation logic —
 * freezing the execution context, coalescing concurrent clicks, pending-record
 * arbitration, and the snapshot plan — lives in `connectorInvocation`; this
 * hook only wires the store and mutation seams into it.
 */
export function useConnectorParameterAddition(
  core: ParameterAdditionCore,
  mutations: ParameterMutationActions,
) {
  const addParameters = useCallback(
    (options: ParameterAdditionOptions = {}): Promise<ParameterAdditionResult> =>
      runConnectorParameterAddition(options, {
        addParametersInFlightRef: core.addParametersInFlightRef,
        connectorConfig: core.connectorConfig,
        connectorKnownFamilyKeysRef: core.connectorKnownFamilyKeysRef,
        currentProjectUndoKey: core.currentProjectUndoKey,
        didProjectChange: core.didProjectChange,
        expectedDocumentIdentity: core.expectedDocumentIdentity,
        menuConfig: core.menuConfig,
        probePendingOperation: core.probePendingOperation,
        resumeExactPendingConnectorOperation: core.resumeExactPendingConnectorOperation,
        setActiveConnectorOperation: core.setActiveConnectorOperation,
        setConnectorParameterUndoPlan: core.setConnectorParameterUndoPlan,
        setConnectorSelectionBlockedByMismatch: core.setConnectorSelectionBlockedByMismatch,
        setIsAdding: core.setIsAdding,
        showError: core.showError,
        showInfo: core.showInfo,
        showSuccess: core.showSuccess,
        showWarning: core.showWarning,
        startParameterProgress: core.startParameterProgress,
        trackPendingOperationToTerminal: core.trackPendingOperationToTerminal,
        updateParameterProgressFromHost: core.updateParameterProgressFromHost,
        addAsProjectParameters: mutations.addAsProjectParameters,
        addToConfiguredFamilyCategories: mutations.addToConfiguredFamilyCategories,
      }),
    [
      core.addParametersInFlightRef,
      core.connectorConfig,
      core.connectorKnownFamilyKeysRef,
      core.currentProjectUndoKey,
      core.didProjectChange,
      core.expectedDocumentIdentity,
      core.menuConfig,
      core.probePendingOperation,
      core.resumeExactPendingConnectorOperation,
      core.setActiveConnectorOperation,
      core.setConnectorParameterUndoPlan,
      core.setConnectorSelectionBlockedByMismatch,
      core.setIsAdding,
      core.showError,
      core.showInfo,
      core.showSuccess,
      core.showWarning,
      core.startParameterProgress,
      core.trackPendingOperationToTerminal,
      core.updateParameterProgressFromHost,
      mutations.addAsProjectParameters,
      mutations.addToConfiguredFamilyCategories,
    ],
  );

  return addParameters;
}
